import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../api';
import { link, navigate } from '../router';
import { Button, Field, Input } from '../ui';
import { ArrowRightIcon, Wordmark } from '../icons';

export function AuthView({
  mode,
  onAuthenticated,
}: {
  mode: 'login' | 'signup';
  /** Reloads the session before navigating, so the panel never renders empty. */
  onAuthenticated: () => void | Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'signup') await api.signup({ email, password, companyName });
      else await api.login({ email, password });
      await onAuthenticated();
      navigate('/evidence');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-5 py-12">
      <a {...link('/')} className="mb-8 flex items-center gap-2 font-medium tracking-tight">
        <span className="text-accent">
          <Wordmark size={20} />
        </span>
        Vouchkeep
      </a>

      <form onSubmit={submit} className="card w-full max-w-sm p-6">
        <h1 className="text-[19px]">
          {mode === 'signup' ? 'Create your evidence bank' : 'Sign in'}
        </h1>
        <p className="mt-1 text-[13px] text-muted">
          {mode === 'signup'
            ? 'One account per company for now.'
            : 'Welcome back.'}
        </p>

        <div className="mt-6 space-y-4">
          {mode === 'signup' ? (
            <Field label="Company name">
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme Inc"
                autoComplete="organization"
                required
              />
            </Field>
          ) : null}

          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              required
            />
          </Field>

          <Field
            label="Password"
            hint={
              mode === 'signup' ? (
                'At least 12 characters.'
              ) : (
                <a {...link('/forgot')} className="text-accent">
                  Forgot your password?
                </a>
              )
            }
            error={error}
          >
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
              minLength={mode === 'signup' ? 12 : undefined}
            />
          </Field>
        </div>

        <Button
          type="submit"
          variant="primary"
          icon={ArrowRightIcon}
          busy={busy}
          className="mt-6 w-full"
        >
          {mode === 'signup' ? 'Create account' : 'Sign in'}
        </Button>

        <p className="mt-4 text-center text-[13px] text-muted">
          {mode === 'signup' ? (
            <>
              Already have an account?{' '}
              <a {...link('/login')} className="text-accent">
                Sign in
              </a>
            </>
          ) : (
            <>
              No account yet?{' '}
              <a {...link('/signup')} className="text-accent">
                Create one
              </a>
            </>
          )}
        </p>
      </form>

      {mode === 'signup' ? (
        <p className="mt-6 max-w-sm text-center text-[12.5px] leading-relaxed text-faint">
          Sign-in is email and password. A magic link would be one fewer secret to lose, and it is
          the next thing here — the reset flow already sends mail, so the difference is a decision
          rather than a dependency.
        </p>
      ) : null}
    </div>
  );
}
